import { MAX_SPACE_FOLDER_DEPTH, SpaceNavigationRoot } from 'shared';

import { SpaceSelection } from '../types/data-room.types';

const SIDEBAR_ROW_PADDING_CLASS = [
  'p-2',
  'py-2 pl-8 pr-2',
  'py-2 pl-[54px] pr-2',
  'py-2 pl-[78px] pr-2',
  'py-2 pl-[100px] pr-2',
  'py-2 pl-[122px] pr-2',
  'py-2 pl-[144px] pr-2',
  'py-2 pl-[165px] pr-2',
];

export const SIDEBAR_MIN_WIDTH = 250;
export const SIDEBAR_MAX_WIDTH = 290;

export type SidebarExpanded = Record<string, string[]>;

export const sidebarRowPaddingClass = (depth: number): string => {
  const index = Math.min(Math.max(depth, 0), MAX_SPACE_FOLDER_DEPTH);

  return SIDEBAR_ROW_PADDING_CLASS[index] ?? 'py-2 pl-[165px] pr-2';
};

export const visibleSidebarDepth = (expanded: SidebarExpanded): number =>
  Object.values(expanded).reduce(
    (maxDepth, ancestors) =>
      ancestors.every((id) => id in expanded) ? Math.max(maxDepth, ancestors.length + 1) : maxDepth,
    0,
  );

export const sidebarWidth = (visibleDepth: number): number => {
  const depth = Math.min(Math.max(visibleDepth, 0), MAX_SPACE_FOLDER_DEPTH);
  const extra = Math.round(
    ((SIDEBAR_MAX_WIDTH - SIDEBAR_MIN_WIDTH) * depth) / MAX_SPACE_FOLDER_DEPTH,
  );

  return SIDEBAR_MIN_WIDTH + extra;
};

export const nodeIdOf = (selection: SpaceSelection): string =>
  selection.folderId ?? selection.space.id;

export const selectedNodeId = (selection?: SpaceSelection): string | undefined =>
  selection ? nodeIdOf(selection) : undefined;

const pathOf = (selection: SpaceSelection): string[] => [
  selection.space.id,
  ...selection.breadcrumbs.map(({ id }) => id),
];

export const ancestorsOf = (selection: SpaceSelection): string[] => pathOf(selection).slice(0, -1);

const latestRootOf = (selection: SpaceSelection, roots: SpaceNavigationRoot[]) =>
  roots.find(({ id, kind }) => id === selection.space.id && kind === selection.space.kind);

// Whether the open space is gone from navigation that has loaded, as when it was deleted elsewhere;
// a navigation still loading says nothing, so the caller passes only roots it has.
export const isSelectedSpaceGone = (
  selection: SpaceSelection,
  roots: SpaceNavigationRoot[],
): boolean => !latestRootOf(selection, roots);

export const withLatestSpace = (
  selection: SpaceSelection,
  roots: SpaceNavigationRoot[],
): SpaceSelection => {
  const latest = latestRootOf(selection, roots);

  return latest ? { ...selection, space: latest } : selection;
};

export const expandedPathOf = (selection: SpaceSelection): SidebarExpanded => {
  const path = pathOf(selection);

  return Object.fromEntries(path.map((id, index) => [id, path.slice(0, index)]));
};
